import { hasStoredImage } from 'src/app/shared/utils/image-url';
import { collectFlags } from '../../../services/draft-features';

/**
 * Normalize a stored draft item into the shape DraftPreviewDialogComponent renders.
 *
 * set-insert drafts already store that flat shape (getModel output) and have no
 * `instanceValue`, so they pass through with a derived `flags` list added. transfer/
 * unload drafts store the raw stock row (nested `product`/`combination`, movement qty
 * in `instanceValue`), so project those fields onto the preview shape. Restore still
 * uses the full stored row — this projection is display-only.
 */
export function toDraftPreviewItem(item: any): any {
  // `instanceValue` is the movement-quantity marker; only transfer/unload rows have it.
  if (!item || item.instanceValue == null) {
    // set-insert: flags live as top-level booleans on the model row; derive them
    // generically (non-boolean fields like `editable` are objects, so ignored).
    return { ...item, flags: collectFlags(item) };
  }
  const product = item.product || {};
  const combination = item.combination || {};
  // `hasStoredImage`, not truthiness: the `cardImageNotAvailable` sentinel is a truthy string,
  // so a bare `? :` here handed the preview a cardData for a row with NO picture -- rendered as
  // the bundled placeholder through `appImageUrl`, while a null row one card over rendered the
  // dialog's `image_not_supported` icon. Two renderings of one condition, in one grid.
  const cardData = hasStoredImage(product.cardData?.image)
    ? product.cardData
    : (hasStoredImage(product.imageName) ? { image: product.imageName } : undefined);
  return {
    name: product.name ?? product.cardData?.name,
    cardData,
    quantity: item.instanceValue, // the entered movement qty, not the stock-level `quantity`
    rarity: product.rarity,
    expansion: product.expansion,
    conditionRow: combination.condition,
    languageRow: combination.language,
    // Two warehouses for transfer: source (from stock) and destination (user input).
    // The preview chooses which to render via the feature's warehouse display mode.
    warehouseFrom: item.warehouse,
    warehouseTo: item.instanceValueWarehouse,
    // Kept for any 'single' fallback consumer; destination first, else source.
    warehouseRow: item.instanceValueWarehouse ?? item.warehouse,
    flags: collectFlags(combination),
    purchasePrice: item.actualPrice,
    comment: item.comments,
  };
}
